import { Stack } from '@mui/material';
import { t } from 'i18next';
import { ComponentLoader } from '../../../common/components/ComponentLoader';
import { BaseController, BaseState } from '../../../common/controllers/BaseController';
import { FilterStatus } from '../../../common/enums/FilterStatus';
import { Severity } from '../../../common/enums/Severity';
import { SeverityFilter } from '../common/components/SeverityFilter';
import { FilterToggle } from '../components/FilterToggle';
import { IssueGroup } from '../components/IssueGroup';
import { IssuesListSkeleton } from '../components/skeletons/IssuesListSkeleton';
import { Issue } from '../models/Issue';
import { ReportIssuesRepository } from '../services/ReportIssuesRepository';
import { ReportExportState } from '../../../exports/triggers/states/ReportExportState';

type Props = {
  reportId: number;
  selectedIssueNumber: number | null;
  filterToggleValue: boolean;

  onIssuesLoaded: () => void;
  onFilterToggled: (checked: boolean) => void;
  onIssueFiltered: () => void;
};

type State = BaseState & {
  initialLoad: boolean;
  loadingIssues: boolean;

  showFilterToggle: boolean;

  nonce: number;
};

type IssueGroupProps = {
  label: string;
  tooltip: string;
  issues: Issue[];
  severityFilters?: SeverityFilter[];
};

export class IssuesListController extends BaseController<Props, State> {
  private readonly repository = new ReportIssuesRepository();

  private issueGroups: IssueGroupProps[];
  private exportState: ReportExportState;

  constructor(props: Props, context: unknown) {
    super(props, context);

    this.issueGroups = [];
    this.exportState = ReportExportState.getInstance();

    this.state = {
      ...super.getBaseState(),
      loadingIssues: true,
      initialLoad: true,

      // Shows or hides the filter toggle depending on the existence of filtered issues
      showFilterToggle: false,

      // Nonce to re-mount the issues list on refresh
      nonce: 0,
    };
  }

  componentDidMount(): void {
    super.componentDidMount();

    this.fetchIssues();
  }

  componentDidUpdate(prevProps: Readonly<Props>): void {
    if (prevProps.filterToggleValue !== this.props.filterToggleValue) {
      this.fetchIssues(true);
    }
  }

  componentWillUnmount(): void {
    super.componentWillUnmount();

    this.repository.cancelAllRequests();
  }

  render() {
    const { initialLoad, loadingIssues, showFilterToggle, nonce } = this.state;
    const { selectedIssueNumber, filterToggleValue, onIssueFiltered } = this.props;

    if (initialLoad) {
      return <IssuesListSkeleton />;
    }

    if (this.issueGroups.length === 0) {
      return <></>;
    }

    return this.renderBase(
      <Stack className="issues-list" gap={2}>
        {showFilterToggle && (
          <FilterToggle initialLoad={initialLoad} disabled={loadingIssues} value={filterToggleValue} onChange={this.props.onFilterToggled} />
        )}
        {this.issueGroups.map(({ label, tooltip, issues, severityFilters }, index) => (
          <ComponentLoader key={`${label}_${nonce}`} loading={loadingIssues}>
            <IssueGroup
              title={label}
              tooltip={tooltip}
              issues={issues}
              severityFilters={severityFilters ?? []}
              index={index}
              selectedIssueNumber={selectedIssueNumber}
              onIssueFiltered={onIssueFiltered}
            />
          </ComponentLoader>
        ))}
      </Stack>
    );
  }

  private async fetchIssues(filterToggleChanged?: boolean) {
    try {
      this.setState({ loadingIssues: true });

      const issues = await this.repository.getIssues(this.props.reportId, this.props.filterToggleValue);
      // Only run this check if the filter toggle changed
      if (filterToggleChanged) {
        const availableInstances = issues.map((issue) => issue.getInstances()).flat();
        this.exportState.checkAvailableInstances(availableInstances);
      }

      const onlyFullyKeptIssues = issues.every((i) => i.getFilterStatus() === FilterStatus.FULLY_KEPT);
      this.buildMaps(issues);

      this.props.onIssuesLoaded();

      this.setState((prev) => ({ loadingIssues: false, initialLoad: false, showFilterToggle: !onlyFullyKeptIssues, nonce: prev.nonce + 1 }));
    } catch (e: unknown) {
      this.setState({ loadingIssues: false });
      this.handleError(e);
    }
  }

  private buildMaps(issues: Issue[]) {
    const severityMap: Record<Severity, Issue[]> = {
      [Severity.HIGH]: [],
      [Severity.MED]: [],
      [Severity.LOW]: [],
      [Severity.PASSED]: [],
      [Severity.NA]: [],
      [Severity.DISABLED]: [],
      [Severity.FILTERED]: [],
      [Severity.NO_WARNING]: [],
    };
    for (const issue of issues) {
      severityMap[issue.getSeverity()].push(issue);
    }
    const highIssues = severityMap[Severity.HIGH];
    const mediumIssues = severityMap[Severity.MED];
    const lowIssues = severityMap[Severity.LOW];
    this.issueGroups = [
      {
        label: t('failed'),
        tooltip: t('severity.tooltip.priority'),
        issues: [...highIssues, ...mediumIssues, ...lowIssues],
        severityFilters: [
          { severity: Severity.HIGH, count: highIssues.length },
          { severity: Severity.MED, count: mediumIssues.length },
          { severity: Severity.LOW, count: lowIssues.length },
        ],
      },
      {
        label: t('severity.label.passed'),
        tooltip: t('severity.tooltip.passed'),
        issues: [...severityMap[Severity.PASSED]],
      },
      {
        label: t('severity.label.disabled'),
        tooltip: t('severity.tooltip.disabled'),
        issues: [...severityMap[Severity.DISABLED]],
      },
      {
        label: t('severity.label.filtered'),
        tooltip: t('severity.tooltip.filtered'),
        issues: [...severityMap[Severity.FILTERED]],
      },
      {
        label: t('severity.label.na'),
        tooltip: t('severity.tooltip.NA'),
        issues: [...severityMap[Severity.NA]],
      },
    ].filter(({ issues }) => issues.length !== 0);
  }
}
